import React, { useEffect, useRef, useState } from 'react';
import 'src/style/conversationsTab.css';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import getColorCode from 'src/utils/GetThemeColor';
import CustomMdEditor from 'src/utils/CustomMdEditor';
import MdDataPreview from 'src/utils/MdDataPreview';
import { useMediaQuery } from '@mui/material';
import { ReactComponent as TicketConversationicon } from 'src/Images/icons/common/TicketConversationicon.svg';
import { ReactComponent as BellIcon } from 'src/Images/icons/common/bellIcon.svg';
import { ReactComponent as ConversationIcon } from 'src/Images/icons/common/conversationIcon.svg';
import { ReactComponent as MessageIcon } from 'src/Images/icons/common/messageIcon.svg';
import { ReactComponent as TruckIcon } from 'src/Images/icons/common/truckIcon.svg';
import {
  getVehicleTrackingComments,
  createCommentForVehicleTracking,
  uploadVehicleTrackingAttachments,
} from 'src/redux/Actions/VehicleTracking';

const getUploadedUrl = (res) => res?.[0]?.attachment_url;

const getAttachmentMarkdown = (fileName, fileType, uploadedUrl) =>
  fileType?.startsWith('image/')
    ? `![${fileName}](${uploadedUrl})`
    : `[${fileName}](${uploadedUrl})`;

const formatCommentTime = (timestamp) =>
  new Date(timestamp * 1000).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export default function ConversationsTab() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const tenantTheme = authReducer.userData.tenant?.theme;
  const themeColor = getColorCode(tenantTheme);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const { vehicleTrackingComments } = vehicleTrackingReducer;

  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [mentionedProfiles, setMentionedProfiles] = useState([]);

  const filesToUpload = useRef([]);

  const isEmpty = !message.trim();

  useEffect(() => {
    dispatch(
      getVehicleTrackingComments(tenantOrganisationId, vehicleTrackingNumber),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  const handleFilesChange = ({
    file,
    replaceTempPreviewWithUrl,
    imagePlaceholder,
  }) => {
    filesToUpload.current.push({
      file,
      replaceTempPreviewWithUrl,
      pendingUploadMarker: imagePlaceholder,
    });
  };

  const handleMentionSelect = (profile) => {
    setMentionedProfiles((prev) => [...prev, profile]);
  };

  const handleCreateComment = async () => {
    if (isEmpty || isSaving) return;
    setIsSaving(true);

    try {
      let finalMessage = message;

      if (filesToUpload.current.length > 0) {
        await Promise.all(
          filesToUpload.current.map(
            async ({
              file,
              replaceTempPreviewWithUrl,
              pendingUploadMarker,
            }) => {
              const attachmentData = new FormData();
              attachmentData.append(
                'vehicle_tracking_number',
                vehicleTrackingNumber,
              );
              attachmentData.append(
                'tenant_organisation_id',
                tenantOrganisationId,
              );
              attachmentData.append('workflow_stage_id', workflowStageId);
              attachmentData.append('file', file);

              const uploadRes = await dispatch(
                uploadVehicleTrackingAttachments(attachmentData),
              );
              const uploadedUrl = getUploadedUrl(uploadRes);
              if (!uploadedUrl) return;

              replaceTempPreviewWithUrl(uploadedUrl);

              if (pendingUploadMarker) {
                finalMessage = finalMessage.replace(
                  pendingUploadMarker,
                  getAttachmentMarkdown(file.name, file.type, uploadedUrl),
                );
              }
            },
          ),
        );
        filesToUpload.current = [];
      }

      // A mention counts only while its @name is still present in the message
      const mentionedProfileIds = [
        ...new Set(
          mentionedProfiles
            .filter((profile) => finalMessage.includes(`@${profile.name}`))
            .map((profile) => profile.id),
        ),
      ];

      await dispatch(
        createCommentForVehicleTracking({
          tenant_organisation_id: tenantOrganisationId,
          vehicle_tracking_number: vehicleTrackingNumber,
          message: finalMessage,
          mentioned_profile_ids: mentionedProfileIds,
        }),
      );

      await dispatch(
        getVehicleTrackingComments(tenantOrganisationId, vehicleTrackingNumber),
      );

      setMessage('');
      setMentionedProfiles([]);
      filesToUpload.current = [];
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="div-flex-column h-100">
      <div
        className={`div-flex-row ${
          isMobile
            ? 'div-align-center border-bottom vertical-padding-10 horizontal-padding-10'
            : 'border-top border-bottom div-space-between div-align-center vertical-padding-5 horizontal-padding-10'
        }`}
      >
        {isMobile ? (
          <div className="div-flex-row div-align-center cg-10">
            <MessageIcon
              className={`theme-card-header-${tenantTheme} icon-size-sidebar conversation-header-icon`}
            />
            <div className="text-md-outfit text-medium-bold">Conversations</div>
          </div>
        ) : (
          <>
            <div className="text-md-outfit text-medium-bold">Conversations</div>
            <MessageIcon
              className={`theme-card-header-${tenantTheme} icon-size-sidebar conversation-header-icon`}
            />
          </>
        )}
      </div>
      <div className="comment-tab-outer-container">
        {!vehicleTrackingComments.length ? (
          <div className="order-conversation-empty">
            <div className="order-conversation-empty-content">
              <div className=" div-flex-column div-align-center rg">
                <MessageIcon
                  className={`theme-card-header-${tenantTheme} order-conversation-empty-feature-icon`}
                />
                <div className="order-conversation-empty-title">
                  Start the conversation{' '}
                </div>
              </div>
              <div className="order-conversation-empty-description">
                Start a conversation to ask questions, share updates, or clarify
                details. Everyone involved in this vehicle tracking can see and
                reply here.
              </div>
              <div className="order-conversation-empty-divider" />
              <div className="order-conversation-empty-features">
                <div className="order-conversation-empty-feature">
                  <TruckIcon className="order-conversation-empty-feature-icon" />
                  <div className="order-conversation-empty-feature-title">
                    Vehicle Discussions
                  </div>
                  <div className="order-conversation-empty-feature-desc text-nowrap">
                    Discuss vehicle and load specifics.{' '}
                  </div>
                </div>
                <div className="order-conversation-empty-feature">
                  <BellIcon className="order-conversation-empty-feature-icon" />
                  <div className="order-conversation-empty-feature-title">
                    Status Updates
                  </div>
                  <div className="order-conversation-empty-feature-desc text-nowrap">
                    Stay informed at every stage of the vehicle.{' '}
                  </div>
                </div>
                <div className="order-conversation-empty-feature">
                  <ConversationIcon className="order-conversation-empty-feature-icon" />
                  <div className="order-conversation-empty-feature-title">
                    Quick Resolutions
                  </div>
                  <div className="order-conversation-empty-feature-desc text-nowrap">
                    Raise queries and get them resolved fast.{' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={
              isMobile
                ? 'mobile-comment-chat-list-view'
                : 'comment-chat-list-view'
            }
          >
            <div className="order-conversation-timeline">
              {vehicleTrackingComments.map((commentItem) => (
                <div className="order-conversation-row" key={commentItem.id}>
                  <div className="order-conversation-indicator">
                    <span className="order-conversation-node">
                      <span className="order-conversation-icon">
                        <TicketConversationicon />
                      </span>
                    </span>
                  </div>

                  <div className="order-conversation-content">
                    <div className="order-conversation-card">
                      <div
                        className={`order-conversation-header theme-card-header-${tenantTheme}`}
                      >
                        <span className="order-conversation-avatar">
                          {commentItem.created_by?.initial}
                        </span>
                        <span className="text-sm-outfit text-nowrap">
                          {commentItem.created_by?.name}
                        </span>
                        <span className="order-conversation-time">
                          {formatCommentTime(commentItem.created)}
                        </span>
                      </div>
                      <div className="order-conversation-body">
                        <MdDataPreview value={commentItem.message} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="create-chat-section">
          <div className="editor-outer-box">
            <div className="editor-inner-box position-relative">
              <CustomMdEditor
                value={message}
                themeColor={themeColor}
                tenantOrganisationId={tenantOrganisationId}
                vehicleTrackingNumber={vehicleTrackingNumber}
                onChange={setMessage}
                onFilesChange={handleFilesChange}
                onMentionSelect={handleMentionSelect}
              />
            </div>

            <div className="md-editor-footer-actions">
              <button
                type="button"
                disabled={isEmpty || isSaving}
                className="conversation-common-btn"
                style={{ backgroundColor: themeColor }}
                onClick={handleCreateComment}
              >
                {isSaving ? 'Saving...' : 'Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
